// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { createElement } from "react";
import {
  LayoutDashboard,
  Brain,
  Sprout,
  NotebookPen,
  Leaf,
  LineChart,
  Settings,
  Network,
  Users,
  GraduationCap,
  Shield,
  Mic,
  UserRound,
  Building2,
  Plug,
  LogOut,
  Droplets,
  Target,
  FlaskConical,
  PlusCircle,
  ClipboardList,
  DollarSign,
  Award,
  BookOpen,
  Trophy,
  FileText,
  Library,
  Archive,
  GitBranch,
  LogIn,
  ShoppingCart,
  Package,
  CalendarDays,
  Workflow,
  Box,
  Package2,
  MapPin,
  Gauge,
  BarChart2,
  BarChart3,
  AlertTriangle,
  BriefcaseBusiness,
  Compass,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { MembershipTier, ModuleGroup, NavItem } from "@/types/navigation";
import { TOOLS as ARCA_TOOLS } from "@/pages/toolbox/constants/tools";
import { ShotSizeCalculatorTooltipContent } from "@/pages/toolbox/arca/constants/shotSizeCalculatorTooltip";
import { UnitConverterTooltipContent } from "@/pages/toolbox/arca/constants/unitConverterTooltip";
import { ChemicalCalculatorTooltipContent } from "@/pages/toolbox/arca/constants/chemicalCalculatorTooltip";
import { BeneficialInsectsTooltipContent } from "@/pages/toolbox/arca/constants/beneficialInsectsTooltip";
import { AgroChemLogTooltipContent } from "@/pages/toolbox/arca/constants/agroChemLogTooltip";
import { PhotothermalTooltipContent } from "@/pages/toolbox/arca/constants/photothermalTooltip";
import { LightLabTooltipContent } from "@/pages/toolbox/arca/constants/lightLabTooltip";
import { CultivationTargetsTooltipContent } from "@/pages/toolbox/arca/constants/cultivationTargetsTooltip";
import { TankManagementTooltipContent } from "@/pages/toolbox/arca/constants/tankManagementTooltip";
import { FertilityTrackingTooltipContent } from "@/pages/toolbox/arca/constants/fertilityTrackingTooltip";
import { IrrigationSimulatorTooltipContent } from "@/pages/toolbox/arca/constants/irrigationSimulatorTooltip";
import { PpmAnalysisTooltipContent } from "@/pages/toolbox/arca/constants/ppmAnalysisTooltip";
import { CultivationDiaryTooltipContent } from "@/pages/toolbox/arca/constants/cultivationDiaryTooltip";
import { RunoffLoggerTooltipContent } from "@/pages/toolbox/arca/constants/runoffLoggerTooltip";
import { FilterManagementTooltipContent } from "@/pages/toolbox/arca/constants/filterManagementTooltip";
import { AgoraComingSoonTooltipContent } from "@/content/agoraComingSoon";

const tier = (t: MembershipTier) => t;

export const MODULE_GROUPS: ModuleGroup[] = [
  { id: "ai", title: "Intelligence", moduleIds: ["prometheus"] },
  { id: "cultivation", title: "Cultivation", moduleIds: ["crops", "arca", "aroya", "animus", "abacus"] },
  { id: "operations", title: "Operations", moduleIds: ["atlas", "aurum"] },
  { id: "learning", title: "Learning & Network", moduleIds: ["ancora", "agora"] },
  { id: "tools", title: "Tools", moduleIds: ["hands-free"] },
];

const arcaExtraTools: NavItem[] = [
  {
    id: "cultivation-diary",
    label: "Cultivation Diary",
    path: "/arca/diary",
    icon: NotebookPen,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
  {
    id: "runofflogger",
    label: "Runoff Logger",
    path: "/arca/runofflogger",
    icon: Droplets,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
];

/** Sub-routes under Runoff Logger for breadcrumb nesting (not shown in sidebar). */
const runoffSubRoutes: NavItem[] = [
  {
    id: "runoff-logger",
    label: "Logger",
    path: "/arca/runofflogger/logger",
    icon: ClipboardList,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
  {
    id: "runoff-records",
    label: "Records",
    path: "/arca/runofflogger/records",
    icon: Archive,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
];

/** Sub-routes under Filter Management for breadcrumb nesting (not shown in sidebar). */
const filterSubRoutes: NavItem[] = [
  {
    id: "filters-logger",
    label: "Logger",
    path: "/arca/filters/logger",
    icon: ClipboardList,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
  {
    id: "filters-records",
    label: "Records",
    path: "/arca/filters/records",
    icon: Archive,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
  {
    id: "filters-calendar",
    label: "Calendar",
    path: "/arca/filters/calendar",
    icon: CalendarDays,
    requiredTier: tier("Basic"),
    match: "prefix",
  },
];

/** Sub-routes under PPM Analysis for breadcrumb nesting (not shown in sidebar). */
const ppmSubRoutes: NavItem[] = [
  {
    id: "ppms-logger",
    label: "Logger",
    path: "/arca/ppms/logger",
    icon: ClipboardList,
    requiredTier: tier("free"),
    match: "prefix",
  },
  {
    id: "ppms-records",
    label: "Records",
    path: "/arca/ppms/records",
    icon: Archive,
    requiredTier: tier("free"),
    match: "prefix",
  },
];

/** Sub-routes under Atlas Resources for breadcrumb nesting (not shown in sidebar). */
const atlasResourceSubRoutes: NavItem[] = [
  {
    id: "resources-durables",
    label: "Durables",
    path: "/atlas/resources/durables",
    icon: Box,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-consumables",
    label: "Consumables",
    path: "/atlas/resources/consumables",
    icon: Package2,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-personnel",
    label: "Personnel",
    path: "/atlas/resources/personnel",
    icon: Users,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-places",
    label: "Places",
    path: "/atlas/resources/places",
    icon: MapPin,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-plants",
    label: "Plants",
    path: "/atlas/resources/plants",
    icon: Leaf,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-utilities",
    label: "Utilities",
    path: "/atlas/resources/utilities",
    icon: Gauge,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "resources-metrics",
    label: "Facility metrics",
    path: "/atlas/resources/metrics",
    icon: BarChart2,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
];

/** Sub-routes under Atlas Calendar for breadcrumb nesting (not shown in sidebar). */
const atlasCalendarSubRoutes: NavItem[] = [
  {
    id: "calendar-overview",
    label: "Overview",
    path: "/atlas/calendar/overview",
    icon: CalendarDays,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "calendar-gantt",
    label: "Timeline",
    path: "/atlas/calendar/gantt",
    icon: BarChart3,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "calendar-milestones",
    label: "Milestones",
    path: "/atlas/calendar/milestones",
    icon: Target,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "calendar-resources",
    label: "Resources",
    path: "/atlas/calendar/resources",
    icon: Users,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "calendar-conflicts",
    label: "Conflicts",
    path: "/atlas/calendar/conflicts",
    icon: AlertTriangle,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
  },
];

/** Sub-routes under Abacus Quality for breadcrumb nesting (not shown in sidebar). */
const abacusQualitySubRoutes: NavItem[] = [
  {
    id: "quality-batch",
    label: "Assess",
    path: "/abacus/quality/batch",
    icon: Award,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
  {
    id: "quality-history",
    label: "History",
    path: "/abacus/quality/history",
    icon: ClipboardList,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
  {
    id: "quality-trends",
    label: "Trends",
    path: "/abacus/quality/trends",
    icon: LineChart,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
  {
    id: "quality-weightings",
    label: "Weightings",
    path: "/abacus/quality/weightings",
    icon: Award,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
];

/** Sub-routes under Atlas SOP Builder for breadcrumb nesting (not shown in sidebar). */
const atlasSopSubRoutes: NavItem[] = [
  {
    id: "sop-builder",
    label: "Builder",
    path: "/atlas/sop/build",
    icon: FileText,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
  {
    id: "sop-library",
    label: "Library",
    path: "/atlas/sop/library",
    icon: Library,
    requiredTier: tier("Professional"),
    match: "prefix",
  },
];

const arcaToolTooltips: Partial<Record<string, () => React.ReactElement>> = {
  calculator: ShotSizeCalculatorTooltipContent,
  converter: UnitConverterTooltipContent,
  chemical: ChemicalCalculatorTooltipContent,
  ipm: BeneficialInsectsTooltipContent,
  agrochemlog: AgroChemLogTooltipContent,
  photothermal: PhotothermalTooltipContent,
  "light-lab": LightLabTooltipContent,
  targets: CultivationTargetsTooltipContent,
  tanks: TankManagementTooltipContent,
  fertility: FertilityTrackingTooltipContent,
  "irrigation-forecaster": IrrigationSimulatorTooltipContent,
  ppms: PpmAnalysisTooltipContent,
  filters: FilterManagementTooltipContent,
};

const arcaExtraToolTooltips: Partial<Record<string, () => React.ReactElement>> = {
  "cultivation-diary": CultivationDiaryTooltipContent,
  runofflogger: RunoffLoggerTooltipContent,
};

const arcaChildren: NavItem[] = [
  ...ARCA_TOOLS.map((tool) => ({
    id: tool.id,
    label: tool.name,
    path: tool.path,
    icon: tool.icon as LucideIcon,
    requiredTier: tool.requiredTier as MembershipTier,
    match: "prefix" as const,
    ...(arcaToolTooltips[tool.id]
      ? { tooltip: createElement(arcaToolTooltips[tool.id]!) }
      : "tooltip" in tool && tool.tooltip
        ? { tooltip: tool.tooltip as React.ReactNode }
        : {}),
  })),
  ...arcaExtraTools.map((tool) => ({
    ...tool,
    ...(arcaExtraToolTooltips[tool.id]
      ? { tooltip: createElement(arcaExtraToolTooltips[tool.id]!) }
      : {}),
  })),
];

const abacusChildren: NavItem[] = [
  { id: "logger", label: "Log Yield Data", path: "/abacus/logger", icon: PlusCircle, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "records", label: "Yield Records", path: "/abacus/records", icon: ClipboardList, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "prices", label: "Price Settings", path: "/abacus/prices", icon: DollarSign, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "quality", label: "Quality Assessment", path: "/abacus/quality/batch", icon: Award, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "analytics", label: "Yield Insights", path: "/abacus/analytics", icon: LineChart, requiredTier: tier("Advanced"), match: "prefix" },
];

const ancoraChildren: NavItem[] = [
  { id: "atlas", label: "Knowledge Atlas", path: "/ancora", icon: Compass, requiredTier: tier("Basic"), match: "exact" },
  { id: "pro", label: "Ancora Pro", path: "/ancora/pro", icon: Building2, requiredTier: tier("Basic"), match: "prefix" },
  { id: "courses", label: "Legacy Courses", path: "/ancora/courses", icon: BookOpen, requiredTier: tier("Basic"), match: "prefix", adminOnly: true },
  { id: "certifications", label: "Certifications", path: "/ancora/certifications", icon: Award, requiredTier: tier("Basic"), match: "prefix", adminOnly: true },
  { id: "recruiting", label: "Resume Builder", path: "/ancora/recruiting-tool", icon: FileText, requiredTier: tier("Basic"), match: "prefix", adminOnly: true },
];

const animusChildren: NavItem[] = [
  // Animus is wholly Advanced (spec: docs/upgrade-downgrade-flow-spec.md #9)
  { id: "library", label: "Library", path: "/animus/library", icon: Library, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "archive", label: "Archive", path: "/animus/archive", icon: Archive, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "analytics", label: "Analytics", path: "/animus/analytics", icon: BarChart2, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "origins", label: "Origins", path: "/animus/origins", icon: GitBranch, requiredTier: tier("Advanced"), match: "prefix" },
  { id: "traits", label: "Traits", path: "/animus/traits", icon: Target, requiredTier: tier("Advanced"), match: "prefix" },
];

const atlasChildren: NavItem[] = [
  { id: "resources", label: "Resources", path: "/atlas/resources", icon: Package, requiredTier: tier("Professional"), match: "prefix" },
  { id: "sop", label: "SOP Builder", path: "/atlas/sop", icon: FileText, requiredTier: tier("Professional"), match: "prefix" },
  { id: "calendar", label: "Calendar", path: "/atlas/calendar/overview", icon: CalendarDays, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
  { id: "production", label: "Production", path: "/atlas/production", icon: Workflow, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
];

const aurumChildren: NavItem[] = [
  { id: "logs", label: "Logs Engine", path: "/aurum/logs", icon: LogIn, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
  { id: "sops", label: "SOP Vault", path: "/aurum/sops", icon: FileText, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
  { id: "sds", label: "SDS Inventory", path: "/aurum/sds", icon: FlaskConical, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
  { id: "purchases", label: "Purchase Archive", path: "/aurum/purchases", icon: ShoppingCart, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
  { id: "processing", label: "Processing Jobs", path: "/aurum/processing", icon: Package, requiredTier: tier("Professional"), match: "prefix", adminOnly: true },
];

const agoraChildren: NavItem[] = [
  { id: "agora-products", label: "Products", path: "/agora/products", icon: ShoppingCart, requiredTier: tier("Advanced"), match: "prefix", adminOnly: true },
  { id: "agora-professionals", label: "Professionals", path: "/agora/professionals", icon: Users, requiredTier: tier("Advanced"), match: "prefix", adminOnly: true },
  { id: "agora-people", label: "People", path: "/agora/people", icon: BriefcaseBusiness, requiredTier: tier("Advanced"), match: "prefix", adminOnly: true },
];

/** Sub-routes under Agora for breadcrumb nesting (not shown in sidebar). */
const agoraSubRoutes: NavItem[] = [
  {
    id: "agora-my-consultations",
    label: "My consultations",
    path: "/agora/professionals/my-consultations",
    icon: ClipboardList,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-consultant-dashboard",
    label: "Consultant dashboard",
    path: "/agora/professionals/consultants/dashboard",
    icon: LayoutDashboard,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-new-consultant",
    label: "Become a consultant",
    path: "/agora/professionals/consultants/new",
    icon: PlusCircle,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-consultation-detail",
    label: "Consultation",
    path: "/agora/professionals/consultations",
    icon: CalendarDays,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-book",
    label: "Book consultation",
    path: "/agora/professionals/book",
    icon: CalendarDays,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-consultant-profile",
    label: "Consultant profile",
    path: "/agora/professionals/consultants",
    icon: Users,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-pending",
    label: "Application pending",
    path: "/agora/professionals/consultants/pending",
    icon: ClipboardList,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-profile-edit",
    label: "Edit profile",
    path: "/agora/professionals/consultants/profile",
    icon: UserRound,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-certifications",
    label: "Certifications",
    path: "/agora/professionals/consultants/certifications",
    icon: Award,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-admin",
    label: "Administration",
    path: "/agora/professionals/admin",
    icon: Shield,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
  {
    id: "agora-admin-verification",
    label: "Verification review",
    path: "/agora/professionals/admin/verification",
    icon: Shield,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
  },
];

export const PRIMARY_NAV: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: LayoutDashboard,
    requiredTier: tier("Basic"),
    match: "exact",
  },
  {
    id: "prometheus",
    label: "Prometheus",
    path: "/prometheus",
    icon: Brain,
    requiredTier: tier("Professional"),
    match: "prefix",
    breadcrumbLabel: "Prometheus",
  },
  {
    id: "crops",
    label: "Crops",
    path: "/crops",
    icon: Sprout,
    requiredTier: tier("Basic"),
    match: "prefix",
    breadcrumbLabel: "Crops",
  },
  {
    id: "animus",
    label: "Animus",
    path: "/animus",
    icon: Leaf,
    requiredTier: tier("Advanced"),
    match: "prefix",
    children: animusChildren,
  },
  {
    id: "aroya",
    label: "Aura",
    path: "/aura",
    icon: Gauge,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
  {
    id: "abacus",
    label: "Abacus",
    path: "/abacus",
    icon: LineChart,
    requiredTier: tier("Advanced"),
    match: "prefix",
    children: abacusChildren,
  },
  {
    id: "arca",
    label: "Arca",
    path: "/arca",
    icon: Settings,
    requiredTier: tier("free"),
    match: "prefix",
    tooltip:
      "Cultivation toolkit — drip irrigation calculators, runoff and fertigation loggers, IPM guides, filter tracking, and cultivation targets for daily room work.",
    children: arcaChildren,
  },
  {
    id: "atlas",
    label: "Atlas",
    path: "/atlas",
    icon: Network,
    requiredTier: tier("Professional"),
    match: "prefix",
    children: atlasChildren,
  },
  {
    id: "agora",
    label: "Agora",
    path: "/agora",
    icon: Users,
    requiredTier: tier("Advanced"),
    match: "prefix",
    adminOnly: true,
    tooltip: createElement(AgoraComingSoonTooltipContent),
    children: agoraChildren,
  },
  {
    id: "ancora",
    label: "Ancora",
    path: "/ancora",
    icon: GraduationCap,
    requiredTier: tier("Basic"),
    match: "prefix",
    children: ancoraChildren,
  },
  {
    id: "aurum",
    label: "Aurum",
    path: "/aurum",
    icon: Shield,
    requiredTier: tier("Professional"),
    match: "prefix",
    adminOnly: true,
    children: aurumChildren,
  },
  {
    id: "hands-free",
    label: "Hands-Free",
    path: "/hands-free",
    icon: Mic,
    requiredTier: tier("Advanced"),
    match: "prefix",
  },
];

export const ACCOUNT_NAV: NavItem[] = [
  { id: "getting-started", label: "Getting Started", path: "/getting-started", icon: Compass, requiredTier: tier("free"), match: "prefix" },
  { id: "help", label: "Help & tutorials", path: "/help", icon: BookOpen, requiredTier: tier("free"), match: "prefix" },
  { id: "profile", label: "Profile", path: "/profile", icon: UserRound, requiredTier: tier("free"), match: "prefix" },
  { id: "facilities", label: "Facilities", path: "/profile/facilities", icon: Building2, requiredTier: tier("free"), match: "prefix" },
  { id: "integrations", label: "Integrations", path: "/profile/integrations", icon: Plug, requiredTier: tier("free"), match: "prefix" },
];

export const LOGOUT_NAV: NavItem = {
  id: "logout",
  label: "Logout",
  path: "/logout",
  icon: LogOut,
  requiredTier: tier("free"),
};

/** Flat lookup for breadcrumbs */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => [item, ...(item.children ? flattenNavItems(item.children) : [])]);
}

export const ALL_NAV_ITEMS = [
  ...flattenNavItems([...PRIMARY_NAV, ...ACCOUNT_NAV]),
  ...ppmSubRoutes,
  ...runoffSubRoutes,
  ...filterSubRoutes,
  ...atlasResourceSubRoutes,
  ...atlasSopSubRoutes,
  ...atlasCalendarSubRoutes,
  ...abacusQualitySubRoutes,
  ...agoraSubRoutes,
];

export const DASHBOARD_MODULES_BY_ID = Object.fromEntries(
  PRIMARY_NAV.filter((n) => n.id !== "dashboard").map((n) => [n.id, n]),
);
